"use client"

import type React from "react"
import { useState } from "react"
import { useAuth } from "../../../services/AuthContext"
import { api } from "../../../services/Axios"
import Loader from "../../utils/loader/Loader"
import ConfirmationModal from "../../utils/avisos/ConfirmationModal"
import MessageModal from "../../utils/avisos/MessageModal"
import { FaUser, FaEnvelope, FaUserTag, FaUserEdit, FaTrashAlt, FaUpload } from "react-icons/fa"
import Input from "../../ui/Input"
import PasswordInput from "../../ui/PasswordInput"
import Button from "../../ui/Button"
import { Card, CardHeader, CardTitle, CardContent } from "../../ui/Card"
import Badge from "../../ui/Badge"

const Profile: React.FC = () => {
  const { userData, updateUserData } = useAuth()
  const [nombre, setNombre] = useState<string>(userData?.nombre || "")
  const [password, setPassword] = useState<string>("")
  const [isProfileConfirmationOpen, setIsProfileConfirmationOpen] = useState<boolean>(false)
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState<boolean>(false)
  const [isMessageOpen, setIsMessageOpen] = useState<boolean>(false)
  const [message, setMessage] = useState<string>("")
  const [messageType, setMessageType] = useState<"success" | "error">("success")

  if (!userData) return <Loader />

  const handleUpdateProfile = () => setIsProfileConfirmationOpen(true)

  const confirmUpdateProfile = async () => {
    setIsProfileConfirmationOpen(false)
    try {
      const updateData: any = { nombre }
      if (password) updateData.password = password

      const response = await api.put("/api/auth/user/update", updateData)
      updateUserData({ nombre })
      setMessage(response.data.msg || "Perfil actualizado con éxito")
      setMessageType("success")
    } catch (error) {
      setMessage("Error al actualizar el perfil.")
      setMessageType("error")
    } finally {
      setIsMessageOpen(true)
    }
  }

  const handleDeleteAccount = () => setIsDeleteConfirmationOpen(true)

  const closeMessageModal = () => setIsMessageOpen(false)

  return (
    <div className="min-h-screen bg-gradient-to-b from-white px-4 py-10">
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-extrabold text-gray-900 mb-2">Tu Perfil</h1>
          <div className="mt-3 w-20 h-1 bg-[#2d53da] mx-auto rounded-full"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Avatar */}
          <Card className="md:col-span-1 shadow-lg border-0">
            <CardContent className="p-8 flex flex-col items-center">
              <div className="w-36 h-36 rounded-full bg-[#2d53da] text-white flex items-center justify-center text-5xl font-bold shadow-lg mb-4">
                {nombre
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase()
                  .substring(0, 2)}
              </div>
              <Button variant="outline" size="sm" icon={FaUpload} className="mt-2">
                Cambiar Imagen
              </Button>
              <p className="text-xs text-gray-500 mt-2 text-center">Formatos PNG, JPEG, GIF hasta 1MB</p>
            </CardContent>
          </Card>

          {/* Perfil */}
          <Card className="md:col-span-2 shadow-xl border-0">
            <CardHeader className="bg-[#2d53da] text-white">
              <CardTitle className="text-xl text-white">Detalles del Usuario</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-5">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Estado de la cuenta</span>
                <Badge variant={userData.isVerified ? "success" : "danger"}>
                  {userData.isVerified ? "Verificado" : "No Verificado"}
                </Badge>
              </div>

              <Input
                label="Correo Electrónico"
                value={userData.email || ""}
                icon={<FaEnvelope className="h-5 w-5 text-gray-400" />}
                disabled
                fullWidth
              />

              <Input
                label="Rol del Usuario"
                value={userData.role === "admin" ? "Administrador" : "Vendedor"}
                icon={<FaUserTag className="h-5 w-5 text-gray-400" />}
                disabled
                fullWidth
              />

              <Input
                label="Nombre Completo"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                icon={<FaUser className="h-5 w-5 text-gray-400" />}
                fullWidth
              />

              <PasswordInput
                label="Nueva Contraseña"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                showStrengthIndicator
              />

              <Button
                onClick={handleUpdateProfile}
                icon={FaUserEdit}
                className="mt-4 bg-[#2d53da] hover:bg-[#1e3eb8] text-white shadow-md hover:shadow-lg"
                fullWidth
              >
                Guardar Cambios
              </Button>
            </CardContent>
          </Card>

          {/* Danger Zone */}
          <Card className="md:col-span-3 border-red-300 shadow-md">
            <CardHeader className="bg-red-100">
              <CardTitle className="text-red-700">Zona de Riesgo</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <p className="text-gray-600 mb-4">
                Esta acción eliminará permanentemente tu cuenta y todos tus datos. No podrás revertir este cambio.
              </p>
              <Button variant="danger" icon={FaTrashAlt} onClick={handleDeleteAccount}>
                Eliminar Cuenta Permanentemente
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      <ConfirmationModal
        isOpen={isProfileConfirmationOpen}
        title="Confirmar cambios"
        message="¿Estás seguro de actualizar tu perfil?"
        onConfirm={confirmUpdateProfile}
        onCancel={() => setIsProfileConfirmationOpen(false)}
      />

      <ConfirmationModal
        isOpen={isDeleteConfirmationOpen}
        title="Confirmar eliminación de cuenta"
        message="¿Estás completamente seguro de eliminar tu cuenta? Esta acción no se puede deshacer."
        onConfirm={() => setIsDeleteConfirmationOpen(false)}
        onCancel={() => setIsDeleteConfirmationOpen(false)}
      />

      <MessageModal isOpen={isMessageOpen} message={message} messageType={messageType} onClose={closeMessageModal} />
    </div>
  )
}

export default Profile